// ============================================================================
// SHARED FORM UTILITIES
// ============================================================================

import { FormValidationState } from '../types'

/**
 * Validates a form element and optionally updates validation state
 */
export const validateElement = (
  el: HTMLInputElement,
  show: boolean = true,
  setValidation?: React.Dispatch<React.SetStateAction<FormValidationState>>,
): boolean => {
  if (!el || !el.name) {
    return true
  }
  const toValidate = el.name
  const valid = el.checkValidity()
  if (show && setValidation) {
    setValidation(prev => ({ ...prev, [toValidate]: valid }))
  }
  return valid
}

/**
 * Gets CSS class name based on validation state
 */
export const getValidationClassName = (
  validation: FormValidationState,
  id: string,
): string => {
  switch (validation[id]) {
    case true:
      return 'is-valid'
    case false:
      return 'is-invalid'
    default:
      return ''
  }
}

/**
 * Extracts value from form element based on input type
 */
export const extractFormValue = (el: HTMLInputElement): any => {
  if (!el) return null
  const inputType = el.type
  switch (inputType) {
    case 'checkbox':
    case 'radio':
      return el.checked
    default:
      return el.value
  }
}

/**
 * Creates a standardized change handler for form elements
 */
export const createFormChangeHandler = (
  onValueChange: (name: string, value: any) => void,
  extractValue: (el: HTMLInputElement) => any = extractFormValue,
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || (!e.target.readOnly && !e.target.name)) {
      return
    }
    const el = e.target
    const elementName = el.name
    const newValue = extractValue(el)

    onValueChange(elementName, newValue)
  }
}

/**
 * Creates a standardized blur handler for form elements
 */
export const createFormBlurHandler = (
  validateElement: (el: HTMLInputElement, show?: boolean) => boolean,
) => {
  return (e: React.FocusEvent<HTMLInputElement>) => {
    validateElement(e.target)
  }
}
